import LinkIcon from '@mui/icons-material/Link';
import React, { useRef, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { EditRuntimeParameterDialog } from './EditRuntimeParameterDialog';
import { HDataTable, HTableControlPanel } from 'BaseComponents';
import { SyncParameterDialog } from './SyncParameterDialog';
import { UnsyncParameterDialog } from './UnsyncParameterDialog';
import { UsedInQuestionsSidePanel } from '../UsedInQuestionsSidePanel';
import { cleanRoom } from 'utils/urlConstants';


export const RuntimeParametersTable = ({ orgId, crId }) => {
  const dataContextRef = useRef();
  const dataURL = cleanRoom(orgId).runtimeParameterTaxonomy(crId);

  const [panel, openPanel] = useState({
    edit: false,
    sync: false,
    unsync: false,
    questions: false,
    row: null,
  });

  const getActionMenuOptions = (row) => {
    const options = [
      {
        label: 'Edit',
        order: 1,
        action: () => openPanel({ edit: true, row }),
      },
    ];

    if (row.isSynced) {
      options.push({
        label: 'Stop Syncing',
        order: 2,
        action: () => openPanel({ unsync: true, row }),
      });
    }
    else {
      options.push({
        label: 'Sync Parameter',
        order: 2,
        action: () => openPanel({ sync: true, row }),
      });
    }

    // Return new items sorted ascending by order field (immutability)
    return [...options].sort((a, b) => a.order - b.order);
  };

  const columns = [
    {
      field: 'name',
      name: 'Parameter Name',
      width: 200,
      component: ({ row }) => (
        <Typography variant='body2' fontWeight={500}>
          {row.name}
        </Typography>
      ),
    },
    {
      field: 'type',
      name: 'Type',
      width: 120,
    },
    {
      field: 'displayName',
      name: 'Display Name',
      width: 200,
      component: ({ row }) => row.displayName || '-',
    },
    {
      field: 'description',
      name: 'Description',
      width: 280,
      component: ({ row }) => (
        <Typography noWrap title={row.description}>
          {row.description || '-'}
        </Typography>
      ),
    },
    {
      field: 'format',
      name: 'Format',
      width: 140,
      component: ({ row }) => row.format || '-',
    },
    {
      field: 'exampleValue',
      name: 'Example',
      width: 140,
      component: ({ row }) => row.exampleValue || '-',
    },
    {
      field: 'status',
      name: 'Status',
      width: 150,
      component: ({ row }) => {
        if (row.isSynced) {
          return (
            <Chip
              icon={<LinkIcon sx={{ fontSize: 18, color: 'inherit' }} />}
              label='Synced'
              size='small'
              variant='increase'
              style={{ fontWeight: 600 }}
            />
          );
        }
        return <Chip label='Independent' size='small' />;
      },
    },
    {
      field: 'usageCount',
      name: 'Used In',
      width: 200,
      component: ({ row }) => {
        const crCount = row.usageCount;
        const orgCount = row.orgUsageCount;

        // Only display it if the parameter is synced
        // AND if it is synced for more questions than the one in the clean room
        const showOrgCount = row.isSynced && orgCount > crCount;

        return (
          <Box display='flex' flexDirection='column'>
            {/* Primary: Clean Room Usage */}
            {crCount ? (
              <Typography
                variant='body2'
                sx={{
                  color: (theme) => theme.palette.info.main,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  width: 'max-content',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  openPanel({ questions: true, row });
                }}
              >
                {`${crCount} Question${crCount === 1 ? '' : 's'}`}
              </Typography>
            ) : (
              <Typography variant='body2'>-</Typography>
            )}

            {/* Secondary: Org Usage */}
            {showOrgCount && (
              <Typography variant='caption'>
                {`(of ${orgCount} in Org)`}
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <HDataTable
        // Add filters later if HDataTable supports them via URL configs
        filtersURL=''
        dataURL={dataURL}
        tableName='clean-room-runtime-parameters'
        dataBaseField='runtimeParameters'
        columns={columns}
        actionMenu={getActionMenuOptions}
        subRowsProp='runtimeParameters'
        controlPanel={(
          <HTableControlPanel
            showFiltersButton={false}
            createPermissions={[]}
            dataContextRef={dataContextRef}
            onRefresh={() => dataContextRef.current.refreshListData()}
          />
        )}
        height='auto'
      />
      <UsedInQuestionsSidePanel
        open={panel.questions}
        title={panel.row ? `${panel.row.name} (${panel.row.type})` : ''}
        itemTypeLabel='runtime parameter'
        row={panel.row}
        onClose={() => openPanel({ ...panel, questions: false })}
      />
      <EditRuntimeParameterDialog
        open={panel.edit}
        row={panel.row}
        onClose={() => openPanel({ edit: false, questions: false })}
        onSuccess={() => dataContextRef.current.refreshListData()}
      />

      <SyncParameterDialog
        open={panel.sync}
        row={panel.row}
        onClose={() => openPanel({ ...panel, sync: false })}
        onSuccess={() => dataContextRef.current.refreshListData()}
      />

      <UnsyncParameterDialog
        open={panel.unsync}
        row={panel.row}
        onClose={() => openPanel({ ...panel, unsync: false })}
        onSuccess={() => dataContextRef.current.refreshListData()}
      />

    </>
  );
};
